import { useEffect, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { isInAppBrowser, getBrowserLabel, externalBrowserUrl } from '@/lib/browserEnv';

const DISMISS_KEY = 'sin_inapp_banner_dismissed_v1';

/**
 * Quiet, dismissible top banner shown only inside Facebook / Instagram /
 * TikTok / WebView browsers — the main source of blank-page and auth
 * failures. Helps users open the site in their real browser.
 */
export function InAppBrowserBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isInAppBrowser()) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY)) return;
    } catch { /* noop */ }
    setVisible(true);
  }, []);

  if (!visible) return null;

  const label = getBrowserLabel().replace('-inapp', '').replace(/-/g, ' ');
  const href = externalBrowserUrl();

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setVisible(false);
  };

  return (
    <div
      role="status"
      className="w-full bg-[#0D1B1E]/95 text-[#F4EDE4] border-b border-[#00C896]/30 backdrop-blur supports-[backdrop-filter]:bg-[#0D1B1E]/80"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="max-w-2xl mx-auto flex items-center gap-2 px-3 py-2 text-xs sm:text-sm">
        <span className="flex-1 leading-snug">
          You're viewing this in the <span className="capitalize font-medium">{label}</span> browser.
          For the best experience, open in your browser.
        </span>
        <a
          href={href}
          className="inline-flex items-center gap-1 rounded-full bg-[#00C896] text-[#0D1B1E] px-3 py-1 font-medium whitespace-nowrap"
          onClick={dismiss}
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open
        </a>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="p-1 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
